import { useState } from 'react';
import { Trash2, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useNostrDelete } from '@/hooks/useNostrDelete';
import { useToast } from '@/hooks/useToast';

interface ConfirmDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  label: string;
  onSuccess?: () => void;
}

export function ConfirmDeleteDialog({ open, onOpenChange, eventId, label, onSuccess }: ConfirmDeleteDialogProps) {
  const { toast } = useToast();
  const { mutateAsync: deleteEvent, isPending } = useNostrDelete();
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteEvent({ eventIds: [eventId], reason: `Deleted by author` });
      toast({ title: 'Deleted', description: `${label} has been deleted.` });
      onSuccess?.();
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Delete failed',
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2">
            <Trash2 className="w-5 h-5 text-destructive" />
            Delete {label}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            This will publish a NIP-09 deletion request to Nostr relays. Compliant relays will remove the event, but it may persist on some relays. This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy || isPending}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={busy || isPending}
            className="bg-destructive hover:bg-destructive/90 text-destructive-foreground"
          >
            {busy || isPending ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Deleting...</>
            ) : (
              <><Trash2 className="w-4 h-4 mr-2" />Delete</>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
